import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, User, Send, Check } from 'lucide-react';
import useScrollReveal from '../hooks/useScrollReveal';
import useSeo from '../hooks/useSeo';
import { IMAGES, COMPANY } from '../mock';

const Contact = () => {
  useScrollReveal();
  useSeo({ title: 'Contact LRG Holdings', description: 'Get in touch with LRG Holdings in Gaborone, Botswana for diamond, leather, education and community partnership enquiries.', path: '/contact' });
  const [form, setForm] = useState({ name: '', email: '', message: '', website: '' });
  const [startedAt] = useState(() => Date.now());
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) { setError('Please enter your name.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { setError('Please enter a valid email address.'); return; }
    if (form.message.trim().length < 10) { setError('Please enter a message of at least 10 characters.'); return; }
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, elapsed_ms: Date.now() - startedAt }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Request failed');
      }
      setSent(true);
      setForm({ name: '', email: '', message: '', website: '' });
    } catch (err) {
      setError(err.message === 'Request failed' || err.message === 'Failed to fetch' ? `Something went wrong. Please try again or email us directly at ${COMPANY.email}.` : err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[52vh] min-h-[380px] flex items-end overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <img src={IMAGES.acaciaSunset} alt="Acacia tree at sunset in Botswana" className="w-full h-full object-cover kenburns" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#071627] via-[#071627]/55 to-[#071627]/50" />
        </div>
        <div className="relative z-10 container-lg pb-14">
          <p className="eyebrow-light mb-4 reveal is-visible">Get in Touch</p>
          <h1 className="font-serif text-[#f7f4ee] text-5xl md:text-7xl font-medium">Contact Us</h1>
          <div className="gold-rule mt-8" />
        </div>
      </section>

      <section className="py-24 bg-[#f7f4ee]">
        <div className="container-lg grid lg:grid-cols-12 gap-16">
          {/* Info */}
          <div className="lg:col-span-5 reveal">
            <p className="eyebrow mb-4">LRG Holdings</p>
            <h2 className="font-serif text-4xl text-[#0a1f38] leading-tight mb-6">Let&rsquo;s build something enduring.</h2>
            <div className="gold-rule mb-8" />
            <p className="text-[#3a3f47] font-light leading-relaxed mb-10">
              Whether you are an institution, investor, or partner interested in our work across diamonds, leather, education, or community development, we would be delighted to hear from you.
            </p>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#0a1f38] shrink-0"><Mail className="text-[#c6a15b]" size={18} /></div>
                <div>
                  <p className="text-xs tracking-[0.18em] uppercase text-[#a3813f] mb-1">Email</p>
                  <a href={`mailto:${COMPANY.email}`} className="text-[#0a1f38] hover:text-[#a3813f] transition-colors break-all">{COMPANY.email}</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#0a1f38] shrink-0"><MapPin className="text-[#c6a15b]" size={18} /></div>
                <div>
                  <p className="text-xs tracking-[0.18em] uppercase text-[#a3813f] mb-1">Location</p>
                  <p className="text-[#0a1f38]">{COMPANY.location}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#0a1f38] shrink-0"><User className="text-[#c6a15b]" size={18} /></div>
                <div>
                  <p className="text-xs tracking-[0.18em] uppercase text-[#a3813f] mb-1">Founder</p>
                  <p className="text-[#0a1f38]">{COMPANY.founder}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7 reveal">
            <div className="bg-white p-10 shadow-sm">
              <h3 className="font-serif text-3xl text-[#0a1f38] mb-2">Drop us a line</h3>
              <p className="text-[#6b6f76] font-light mb-8">We typically respond within two business days.</p>
              {sent ? (
                <div data-testid="contact-success" className="flex flex-col items-center text-center py-14">
                  <div className="w-16 h-16 rounded-full bg-[#c6a15b]/15 flex items-center justify-center mb-5"><Check className="text-[#a3813f]" size={30} /></div>
                  <h4 className="font-serif text-2xl text-[#0a1f38] mb-2">Thank you</h4>
                  <p className="text-[#6b6f76] font-light">Your message has been received. We typically respond within two business days.</p>
                  <button onClick={() => setSent(false)} className="btn-outline mt-8">Send Another</button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-6 relative" noValidate data-testid="contact-form">
                  <div>
                    <label htmlFor="name" className="block text-xs tracking-[0.18em] uppercase text-[#6b6f76] mb-2">Name *</label>
                    <input data-testid="contact-name-input" id="name" name="name" autoComplete="name" required minLength={2} maxLength={200} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-[#d9d3c8] bg-[#faf8f4] px-4 py-3.5 text-[#0a1f38] focus:outline-none focus:border-[#c6a15b] transition-colors" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs tracking-[0.18em] uppercase text-[#6b6f76] mb-2">Email *</label>
                    <input data-testid="contact-email-input" id="email" name="email" autoComplete="email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-[#d9d3c8] bg-[#faf8f4] px-4 py-3.5 text-[#0a1f38] focus:outline-none focus:border-[#c6a15b] transition-colors" />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-xs tracking-[0.18em] uppercase text-[#6b6f76] mb-2">Message *</label>
                    <textarea data-testid="contact-message-input" id="message" name="message" required minLength={10} maxLength={5000} rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full border border-[#d9d3c8] bg-[#faf8f4] px-4 py-3.5 text-[#0a1f38] focus:outline-none focus:border-[#c6a15b] transition-colors resize-none" />
                  </div>
                  <div className="absolute opacity-0 -z-10 h-0 overflow-hidden" aria-hidden="true"><label htmlFor="website">Website</label><input id="website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
                  {error && <p data-testid="contact-error" role="alert" className="text-sm text-red-700 border border-red-200 bg-red-50 px-4 py-3">{error}</p>}
                  <p className="text-[#6b6f76] text-xs font-light">By submitting you agree to our <Link to="/privacy-policy" className="underline underline-offset-2 hover:text-[#a3813f]">Privacy Policy</Link>.</p>
                  <button data-testid="contact-submit-button" type="submit" disabled={sending} className="btn-gold disabled:opacity-60">{sending ? 'Sending' : 'Send Message'} <Send size={15} /></button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
